import {
  Fragment,
  useEffect,
  useState,
  type Dispatch,
  type MouseEventHandler,
  type SetStateAction,
} from "react"
import { Dialog, Transition } from "@headlessui/react"
import { IoIosRemoveCircleOutline } from "react-icons/io"

import QuantitySelector from "@src/components/QuantitySelector"

import { formatCurrency } from "@src/utilities/format-numbers"

interface QuantitySubscriptionWrapperProps {
  handleChangeFrequency: React.ChangeEventHandler<HTMLSelectElement>
  onSetQuantity: Dispatch<SetStateAction<number>>
  quantity: number
  subcriptionFrequencyInWeeks?: number
}
function QuantitySubscriptionWrapper({
  handleChangeFrequency,
  onSetQuantity,
  quantity,
  subcriptionFrequencyInWeeks,
}: QuantitySubscriptionWrapperProps) {
  return (
    <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-8 lg:max-w-xs lg:space-y-0">
      <div className="w-full max-w-[140px] lg:max-w-none">
        <label className="block text-xs font-medium mb-1 text-gray-600">
          Quantity
        </label>
        <QuantitySelector onSetQuantity={onSetQuantity} quantity={quantity} />
      </div>
      <div className="w-full max-w-[140px] lg:max-w-none">
        {subcriptionFrequencyInWeeks && (
          <>
            <label className="block text-xs font-medium text-gray-600">
              Frequency
            </label>
            <select
              className="mt-1 block w-full pl-2 pr-4 py-2 text-sm border border-gray-400 focus:outline-none rounded-md"
              onChange={handleChangeFrequency}
              value={subcriptionFrequencyInWeeks}
            >
              <option value={2}>every 2 weeks</option>
              <option value={4}>every 4 weeks</option>
              <option value={6}>every 6 weeks</option>
              <option value={8}>every 8 weeks</option>
            </select>
          </>
        )}
      </div>
    </div>
  )
}

export interface CartLineItemProps {
  cartPrice: number
  description?: string | null
  frequencyInWeeks?: number
  name: string
  retailPrice: number
  quantity: number
  sku: string
  thumbnailUrl?: string | null
  onChangeFrequency?: (
    sku: string,
    oldFrequencyInWeeks: number,
    newFrequencyInWeeks: number
  ) => void
  onChangeQuantity: (
    sku: string,
    quantity: number,
    frequency: number | null
  ) => void
  onRemoveItem: (sku: string, frequency: number | null) => void
}
export default function CartLineItem({
  cartPrice,
  description,
  frequencyInWeeks,
  name,
  retailPrice,
  quantity,
  sku,
  thumbnailUrl,
  onChangeFrequency,
  onChangeQuantity,
  onRemoveItem,
}: CartLineItemProps) {
  const [tmpQuantity, setTmpQuantity] = useState<number>(quantity)
  const [openRemoveConfirmation, setOpenRemoveConfirmation] =
    useState<boolean>(false)

  const confirmRemoveItem = () => {
    setOpenRemoveConfirmation(true)
  }

  const handleChangeFrequency: React.ChangeEventHandler<HTMLSelectElement> = (
    ev
  ) =>
    onChangeFrequency &&
    frequencyInWeeks &&
    onChangeFrequency(sku, frequencyInWeeks, Number(ev.target.value))

  const handleRemoveItem: MouseEventHandler = () => {
    setOpenRemoveConfirmation(false)
    onRemoveItem(sku, frequencyInWeeks ?? null)
  }

  useEffect(() => {
    onChangeQuantity(sku, tmpQuantity, frequencyInWeeks ?? null)
  }, [tmpQuantity])

  return (
    <>
      <div className="mb-8" data-component="cart-line-item">
        <div className="flex items-start">
          <div
            className="block bg-center bg-cover w-20 before:content-[''] before:block before:pt-[100%] mr-3"
            style={{
              backgroundImage: `url(${
                thumbnailUrl ?? "/images/placeholder.jpeg"
              })`,
            }}
          />
          <div className="grow">
            <h5
              className="font-browser font-bold text-base"
              dangerouslySetInnerHTML={{ __html: name }}
            />
            <h6
              className="font-browser text-gray-600 text-xs md:mb-4"
              dangerouslySetInnerHTML={{
                __html: description ?? "",
              }}
            />
            <div className="font-browser font-medium mb-2 md:hidden">
              <div>
                {formatCurrency(cartPrice)}
                {cartPrice < retailPrice && (
                  <span className="ml-1 font-normal text-xs text-orange line-through">
                    ({formatCurrency(retailPrice)})
                  </span>
                )}
              </div>
            </div>
            <QuantitySubscriptionWrapper
              handleChangeFrequency={handleChangeFrequency}
              onSetQuantity={setTmpQuantity}
              quantity={tmpQuantity}
              subcriptionFrequencyInWeeks={frequencyInWeeks}
            />
          </div>
          <div className="ml-4 md:text-right">
            <div className="hidden md:block">
              <div>{formatCurrency(cartPrice)}</div>
              {cartPrice < retailPrice && (
                <div className="font-normal text-xs text-orange line-through">
                  {formatCurrency(retailPrice)}
                </div>
              )}
            </div>
            <div>
              <button
                className="text-red md:mt-2"
                onClick={() => confirmRemoveItem()}
              >
                <IoIosRemoveCircleOutline className="md:hidden" />
                <span className="hidden text-xs md:inline">Remove</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <Transition.Root show={openRemoveConfirmation} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={setOpenRemoveConfirmation}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-sm sm:w-full sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium font-browser text-black"
                      >
                        Remove {name}?
                      </Dialog.Title>
                      <p className="font-browser text-sm text-gray-600">
                        Are you sure you want to remove {name} from your basket?
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex justify-center w-full border border-black px-4 py-2 bg-black text-base font-medium font-browser text-white hover:bg-white hover:text-black transition-colors focus:outline-none sm:text-sm"
                      onClick={handleRemoveItem}
                    >
                      Remove From Basket
                    </button>
                    <button
                      type="button"
                      className="mt-2 inline-flex justify-center w-full px-4 py-2 text-sm font-browser text-black hover:underline focus:outline-none sm:text-sm"
                      onClick={() => setOpenRemoveConfirmation(false)}
                    >
                      Keep my {name}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
